import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Loans from './pages/Loans';
import LoansAdmin from './pages/admin/LoansAdmin';
import LoanCreationPage from './pages/LoanCreationPage';
import LoanEditPage from './pages/LoanEditPage';
import LoanDetailsPage from './pages/LoanDetailsPage';
import LoanEditPageExtended from './pages/LoanEditPageExtended';
import Invoices from './pages/Invoices';
import ConsultantBilling from './pages/ConsultantBilling';
import ConsultantBillingOverviewPage from './pages/ConsultantBillingOverviewPage';
import ConsultantBillingAdminOverview from './pages/admin/ConsultantBillingAdminOverview';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Logout from './pages/Logout';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/layout/Layout';
import Analytics from './pages/admin/Analytics';
import WithdrawalPassed from './pages/admin/WithdrawalPassed';
import PaymentConfirmed from './pages/admin/PaymentConfirmed';
import MyBilling from './pages/MyBilling';
import ConsultantBillingAdmin from './pages/admin/ConsultantBillingAdmin';
import Settings from './pages/Settings';
import BankAnalytics from './pages/admin/BankAnalytics';
import Banks from './pages/admin/Banks'; // Import Banks component
import Referrers from './pages/admin/Referrers'; // Import Referers component
import ProtectedRoute from './components/auth/ProtectedRoute';
import MyAnalytics from './pages/MyAnalytics'; // Import MyAnalytics component
import { colors } from './theme/tokens/colors';

const antTheme = {
  token: {
    colorPrimary: colors.brand.primary,
    colorSuccess: colors.status.success,
    colorWarning: colors.status.warning,
    colorError: colors.status.error,
    colorInfo: colors.status.info,
    colorTextBase: colors.text.dark,
    colorBgBase: colors.background.light,
  },
};

const App = () => {
  return (
    <ConfigProvider theme={antTheme}>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/logout" element={<Logout />} />
            
            {/* Protected Routes */}
            <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/my-analytics" element={<MyAnalytics />} />
              
              {/* Loan Routes */}
              <Route path="/loans" element={<Loans />} />
              <Route path="/loans/new" element={<LoanCreationPage />} />
              <Route path="/loans/:id" element={<LoanDetailsPage />} />
              <Route path="/loans/:id/edit" element={<LoanEditPage />} />
              <Route path="/loans/:id/extended-edit" element={<LoanEditPageExtended />} />
              
              {/* Invoice Routes */}
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/consultant-billing" element={<ConsultantBilling />} />
              <Route path="/consultant-billing/:consultantId" element={<ConsultantBilling />} />
              <Route path="/billing/new" element={<MyBilling />} />
              <Route path="/billing" element={<ConsultantBillingOverviewPage />} />
              
              {/* Customer Routes */}
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:id" element={<CustomerDetail />} />

              {/* Settings */}
              <Route path="/settings" element={<Settings />} />

              {/* Accountant */}
              <Route 
                path="accountant/withdrawal-passed" 
                element={
                  <ProtectedRoute 
                    element={<WithdrawalPassed />} 
                    requiredGroup="Accountant" 
                  />
                } 
              />
              <Route 
                path="accountant/payment-confirmed" 
                element={
                  <ProtectedRoute 
                    element={<PaymentConfirmed />} 
                    requiredGroup="Accountant" 
                  />
                } 
              />
              <Route 
                path="accountant/consultant-billing-admin" 
                element={
                  <ProtectedRoute 
                    element={<ConsultantBillingAdmin />} 
                    requiredGroup="Accountant" 
                  />
                } 
              />              
              <Route 
                path="accountant/consultant-billing-admin-overview" 
                element={
                  <ProtectedRoute 
                    element={<ConsultantBillingAdminOverview />} 
                    requiredGroup="Accountant" 
                  />
                } 
              />

              {/* Administration */}
              <Route 
                path="/admin/banks" 
                element={
                  <ProtectedRoute 
                    element={<Banks />} 
                    requiredGroup="Consultant-Admin" 
                    />
                } 
              />
              <Route 
                path="/admin/referrers" 
                element={
                  <ProtectedRoute 
                    element={<Referrers />} 
                    requiredGroup="Consultant-Admin" 
                    />
                } 
              />
              <Route 
                path="/analytics" 
                element={
                  <ProtectedRoute 
                    element={<Analytics />} 
                    requiredGroup="Consultant-Admin" 
                  />
                }
              />
              <Route 
                path="/bank-analytics" 
                element={
                  <ProtectedRoute 
                    element={<BankAnalytics />} 
                    requiredGroup="Consultant-Admin" 
                  />
                }
              />
              <Route 
                path="/admin/loans" 
                element={
                  <ProtectedRoute 
                    element={<LoansAdmin />} 
                    requiredGroup="Consultant-Admin" 
                  />
                }
              />
            </Route>
            {/* Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;